import React, { Fragment, useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import SearchInput from '../SearchInput/SearchInput'
import InputText from '../InputText/InputText'
import useScreen from '../../hooks/useScreen'
import { Transition, Listbox } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/solid'
import clsx from 'clsx'

const DropDownWithSearch = ({
    data,
    keyToShow,
    keyToFind,
    selected,
    setSelectedValue: setSelected,
    name,
    placeholder,
    label,
    required,
    customRender,
    variant,
    className,
    isSmall,
    hasError = true,
    error,
    disabled,
    disabledMessage,
    searchMessage,
    searchPlaceholder,
    isDisableList,
    setIsFromList,
}) => {
    const [disableList, setDisableList] = useState(isDisableList)
    const [query, setQuery] = useState('')
    const [manualInputValue, setManualInputValue] = useState('')
    const inputRef = useRef(null)
    const isBlack = variant === 'black' ? true : false

    const { isMobile } = useScreen()

    // If it is disable then it should show an error
    const showError = disabled ? false : hasError

    // Focus the input when switching to manual input mode
    useEffect(() => {
        if (disableList && inputRef.current) {
            const input = inputRef.current.querySelector('input')
            if (input) {
                input.focus()
                // Move cursor to the end of the text
                setTimeout(() => {
                    input.setSelectionRange(
                        input.value.length,
                        input.value.length,
                    )
                }, 0)
            }
        }
    }, [disableList])

    const enableInput = () => {
        console.log('This will disable the list and show the input')
        // Set the manual input value to the current query
        setManualInputValue(query)
        // Also update the selected value
        setSelected({ value: query })
        setDisableList(true)
        setIsFromList(false)
    }

    const onInputChange = e => {
        let value = e?.target.value
        setManualInputValue(value)
        const res = { value: value }
        setSelected(res)
    }
    const showList = () => {
        setDisableList(false)
        setIsFromList(true)
    }

    const filteredPeople =
        query === ''
            ? data || []
            : data?.length > 0
            ? data.filter(
                  item =>
                      item[keyToFind] &&
                      item[keyToFind]
                          ?.toLowerCase()
                          ?.replace(/\s+/g, '')
                          ?.includes(query.toLowerCase().replace(/\s+/g, '')),
              )
            : []

    return (
        <div className={clsx('relative flex flex-col items-start', className)}>
            {label && (
                <label
                    htmlFor="example"
                    className={clsx(
                        'text-sm font-medium mb-2',
                        isBlack ? 'text-white' : 'text-textDarkerGray',
                    )}
                >
                    {label}
                    {required && (
                        <span className="text-sm font-semibold text-error">
                            {' '}
                            *
                        </span>
                    )}
                </label>
            )}

            {disableList ? (
                <div ref={inputRef} className="w-full">
                    <InputText
                        placeholder={searchPlaceholder}
                        onChange={onInputChange}
                        additionalClass="w-full relative"
                        error={error}
                        showExtraInfo={false}
                        value={manualInputValue}
                        icon={
                            <div className="absolute inset-y-0 right-4 flex items-center max-w-15">
                                <ChevronDownIcon
                                    className={clsx(
                                        'h-5 min-w-5 w-5 min-h-5',
                                        isBlack
                                            ? 'text-black30'
                                            : 'text-black70',
                                    )}
                                    onClick={showList}
                                    aria-hidden="true"
                                />
                            </div>
                        }
                    />
                </div>
            ) : (
                <Listbox name={name} value={selected} onChange={setSelected}>
                    <div
                        className={clsx(
                            'relative w-full max-w-full',
                            isSmall ? 'h-12' : 'h-auto',
                        )}
                    >
                        <div
                            className={clsx(
                                'relative flex flex-row items-center justify-between w-full px-4 py-3 cursor-default overflow-hidden  text-left outline-none border sm:text-sm',
                                disabled
                                    ? 'text-textDarkGray bg-grayBackground'
                                    : isBlack
                                    ? 'bg-black70 border-black60'
                                    : 'bg-white border-grayLines',
                                isSmall ? 'h-12 rounded-xl' : 'rounded-xl',
                            )}
                        >
                            <Listbox.Button className="w-full flex-row flex-grow p-0 h-9 justify-between">
                                <div className="w-full flex-grow overflow-hidden truncate h-9 flex items-center justify-start max-w-85">
                                    {selected && customRender ? (
                                        <>{customRender(selected)}</>
                                    ) : (
                                        <div
                                            id={name}
                                            className={clsx(
                                                'w-full text-left text-sm outline-none font-normal',
                                                isBlack
                                                    ? 'placeholder-black30 text-white bg-black70'
                                                    : 'text-textDarkerGray bg-white',
                                            )}
                                        >
                                            {data?.length > 0 && selected ? (
                                                selected?.[keyToShow]
                                            ) : (
                                                <input
                                                    className={clsx(
                                                        'w-full',
                                                        isBlack && 'bg-black70',
                                                    )}
                                                    onChange={onInputChange}
                                                    placeholder={placeholder}
                                                    disabled={disabled}
                                                />
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="absolute inset-y-0 right-0 flex items-center max-w-15">
                                    <ChevronDownIcon
                                        className={clsx(
                                            'h-5 min-w-5 w-5 min-h-5',
                                            isBlack
                                                ? 'text-black30'
                                                : 'text-black70',
                                        )}
                                        // onClick={showList}
                                        aria-hidden="true"
                                    />
                                </div>
                            </Listbox.Button>
                        </div>
                        <Transition
                            as={Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                            afterLeave={() => setQuery('')}
                        >
                            <Listbox.Options
                                className={clsx(
                                    'relative mt-1 max-h-96 rounded-xl text-base focus:outline-none sm:text-sm shadow-md z-100',
                                    isMobile ? 'w-full' : 'w-4/5 ml-auto ',
                                    isBlack ? 'bg-black70' : 'bg-white',
                                    // disableList && 'hidden',
                                )}
                            >
                                <div
                                    className="sticky top-0 w-full h-16 rounded-t-xl px-3 py-2 bg-grayBackground border border-grayLines space-between"
                                    onKeyDown={e => {
                                        // Prevent Listbox from capturing Space (select option) so the user can type spaces in the search
                                        if (e.key === ' ') {
                                            e.stopPropagation()
                                        }
                                    }}
                                >
                                    <SearchInput
                                        key="searchData"
                                        placeholder="Search"
                                        value={query}
                                        setValue={setQuery}
                                        name="searchData"
                                    />
                                </div>
                                {filteredPeople.length === 0 ? (
                                    <div
                                        className={clsx(
                                            'relative cursor-default select-none py-2 px-4',
                                            isBlack
                                                ? 'text-white'
                                                : 'text-black70',
                                        )}
                                    >
                                        {query !== ''
                                            ? 'Nothing found.'
                                            : 'No items available.'}
                                    </div>
                                ) : (
                                    <div className="max-h-64 overflow-y-auto">
                                        {filteredPeople.map((item, index) => (
                                            <Listbox.Option
                                                key={`option-${index}`}
                                                className={({ active }) =>
                                                    `transition-all relative select-none p-2 border-b border-grayLines cursor-pointer ${(isBlack &&
                                                        active &&
                                                        'text-white bg-black50',
                                                    isBlack &&
                                                        !active &&
                                                        'text-white bg-transparent',
                                                    !isBlack &&
                                                        !active &&
                                                        'text-textDarkGray bg-transparent',
                                                    !isBlack &&
                                                        active &&
                                                        'text-textDarkerGray bg-grayBackground')}`
                                                }
                                                value={item}
                                            >
                                                {({
                                                    selected: selectedAct,
                                                    active,
                                                }) => (
                                                    <>
                                                        <span
                                                            className={clsx(
                                                                `block truncate`,
                                                                selectedAct
                                                                    ? 'font-medium'
                                                                    : 'font-normal',
                                                                active &&
                                                                    !isBlack &&
                                                                    'text-textDarkerGray',
                                                                active &&
                                                                    isBlack &&
                                                                    'text-white',
                                                                !active &&
                                                                    isBlack &&
                                                                    'text-white',
                                                                !active &&
                                                                    !isBlack &&
                                                                    'text-textDarkerGray',
                                                            )}
                                                        >
                                                            {item[keyToShow] ||
                                                                ''}
                                                        </span>
                                                    </>
                                                )}
                                            </Listbox.Option>
                                        ))}
                                        {/* <div className='w-full h-16 flex flex-col gap-1 p-3 bg-white border-b border-grayLines space-between'></div> */}
                                    </div>
                                )}
                                <div className="sticky top-0 w-full h-16 flex flex-col gap-1 p-3 bg-white border-t border-grayLines rounded-b-xl space-between">
                                    <p className="text-sm font-normal text-textDarkGray">
                                        {searchMessage}
                                    </p>
                                    <div
                                        className="text-sm font-normal hover:underline text-links cursor-pointer"
                                        onClick={enableInput}
                                    >
                                        Add it manually
                                    </div>
                                </div>
                            </Listbox.Options>
                        </Transition>
                    </div>
                </Listbox>
            )}
            {showError && (
                <p
                    className={clsx(
                        error ? 'visible' : 'invisible',
                        'w-full h-4 text-right text-error font-medium text-xs mt-1',
                    )}
                >
                    {error}
                </p>
            )}
            {disabled && (
                <p
                    className={clsx(
                        disabledMessage ? 'visible' : 'invisible',
                        'w-full h-4 text-left text-textDarkGray font-normal text-xs mt-1 mb-3 lg:mb-0',
                    )}
                >
                    {disabledMessage}
                </p>
            )}
        </div>
    )
}

DropDownWithSearch.propTypes = {
    data: PropTypes.arrayOf(PropTypes.object),
    keyId: PropTypes.string,
    keyToShow: PropTypes.string,
    keyToFind: PropTypes.string,
    selected: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.object,
        PropTypes.any,
    ]),
    setSelectedValue: PropTypes.func.isRequired,
    name: PropTypes.string,
    placeholder: PropTypes.string,
    label: PropTypes.string,
    required: PropTypes.bool,
    disabled: PropTypes.bool,
    customRender: PropTypes.func,
    setIsFromList: PropTypes.func,
    error: PropTypes.string,
    variant: PropTypes.string,
    className: PropTypes.string,
    isSmall: PropTypes.oneOfType([PropTypes.bool, PropTypes.any]),
    hasError: PropTypes.oneOfType([PropTypes.bool, PropTypes.any]),
    disabledMessage: PropTypes.string,
    searchMessage: PropTypes.string,
    searchPlaceholder: PropTypes.string,
    isDisableList: PropTypes.bool,
}

export default DropDownWithSearch
